import { useMutation, useQuery } from "@apollo/client";
import { AppContext } from "App";
import Button from "components/base/Button";
import Errors from "components/base/Errors";
import { Switcher } from "components/base/Form";
import Page from "components/base/Page";
import Spinner from "components/base/Spinner";
import { Alert } from "components/base/Toast";
import { useContext, useState } from "react";
import { useParams } from "react-router-dom";
import http from "utils/http";
import { INTERNAL } from "utils/permissions";
import { FETCH_QC_PREVIEW, SAVE_SIMPLE_REPORT } from "../graphql";

function QCReportPreview() {
  const { id } = useParams();
  const { hasPermission } = useContext(AppContext);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const { loading, error, data } = useQuery(FETCH_QC_PREVIEW, {
    variables: { id },
  });
  const [saveReport, saveReportRes] = useMutation(SAVE_SIMPLE_REPORT, {
    onCompleted(res) {
      Alert(
        "success",
        res.saveSimpleQcReport.qcReport.sharedWithCustomerAt
          ? "Shared with Customer."
          : "Disabled shared with customer.",
      );
    },
    onError(error) {
      Alert("error", error.message);
    },
  });

  if (loading) return <Spinner />;
  if (error) return <Errors error={error} />;

  const { report } = data;
  const productName = report.product.productLine
    ? report.product.productLine.name
    : report.product.name;

  function exportPdf(id, filename) {
    setDownloadingPdf(true);
    http
      .get(`/qc_reports/${id}/`, {
        baseURL: process.env.REACT_APP_SERVER_BASE_URL,
        timeout: 60000,
        responseType: "blob",
        withCredentials: true,
      })
      .then((res) => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        setDownloadingPdf(false);
      })
      .catch((err) => {
        alert(err);
        setDownloadingPdf(false);
      });
  }

  const fromUrl = new URLSearchParams(window.location.search).get("from");
  const defaultBackTo = report.po
    ? `/qc/po-reports/${id}`
    : "/qc/reports";

  return (
    <Page
      className="flex flex-col flex-1 sm:min-h-screen"
      title={`${productName} ✕ ${report.qty}`}
      subtitle={report.factory?.name || "-"}
      backTo={fromUrl || defaultBackTo}
      rightButtons={
        <div className="flex space-x-6 justify-end">
          {hasPermission(INTERNAL) ? (
            <div className="flex items-center space-x-4">
              <label htmlFor="">Share with Customer</label>
              <Switcher
                isOn={report.sharedWithCustomerAt !== null}
                disabled={saveReportRes.loading}
                loading={saveReportRes.loading}
                onChange={() =>
                  saveReport({
                    variables: {
                      id,
                      shareWithCustomer: report.sharedWithCustomerAt === null,
                    },
                  })
                }
              />
            </div>
          ) : null}
          <Button
            loading={downloadingPdf}
            onClick={() =>
              exportPdf(id, `QC Report - ${report.product.number}.pdf`)
            }
          >
            Download PDF
          </Button>
        </div>
      }
    >
      {saveReportRes.loading ? <Spinner text="Updating..." /> : null}
      <div className="flex flex-1 h-screen bg-white">
        <iframe
          title="QC Report"
          className="flex-1 w-full px-4"
          srcDoc={report.html}
        />
      </div>
    </Page>
  );
}

export default QCReportPreview;
